/* eslint-disable react-hooks/immutability, react-hooks/exhaustive-deps */
import { faArrowLeft, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Divider, Drawer, FormControlLabel, IconButton, Stack, Switch, Typography, useMediaQuery, useTheme } from "@mui/material";
import { green, grey, yellow } from "@mui/material/colors";
import { keyframes } from "@mui/system";
import dayjs, { Dayjs } from "dayjs";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { StatusDot } from "../../../components";
import { AttendanceLogEntry, Child, DailyInspection } from "../../../models";
import { useSaveDailyInspectionMutation } from "../../../redux/apiStore";
import { DailyInspectionEditView } from "./DailyInspectionEditView";
import { EditAttendanceForm } from "./EditAttendanceForm";

const blurOut = keyframes`
  from { opacity: 1; filter: blur(0px); }
  to { opacity: 0; filter: blur(8px); }
`;

const blurIn = keyframes`
  from { opacity: 0; filter: blur(8px); }
  to { opacity: 1; filter: blur(0px); }
`;

export enum CDrawerView {
  DAY_DETAIL = "DAY_DETAIL",
  ATTENDANCE_EDIT = "ATTENDANCE_EDIT",
  DAILY_INSPECTION_EDIT = "DAILY_INSPECTION_EDIT",
}

interface DayDetailDrawerProps {
  selectedDate: Dayjs;
  dailyInspection: DailyInspection | undefined;
  attendanceLogEntries: AttendanceLogEntry[];
  children: Child[];
  isOpen: boolean;
  onClose: () => void;
  initialView?: CDrawerView.ATTENDANCE_EDIT | CDrawerView.DAILY_INSPECTION_EDIT;
}

export function DayDetailDrawer(props: DayDetailDrawerProps) {
  const { selectedDate, dailyInspection, attendanceLogEntries, children, isOpen, onClose, initialView } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  // slideOffset: 0 = day detail visible, -100 = edit view visible
  const [slideOffset, setSlideOffset] = useState(0);
  const [activeEditView, setActiveEditView] = useState<CDrawerView.ATTENDANCE_EDIT | CDrawerView.DAILY_INSPECTION_EDIT | null>(null);
  const [isClosed, setIsClosed] = useState<boolean>(dailyInspection?.isClosed ?? false);
  const [drawerTitle, setDrawerTitle] = useState<string>(selectedDate.format("dddd, MMMM D, YYYY"));
  const [titlePhase, setTitlePhase] = useState<"idle" | "out" | "in">("idle");
  const [saveDailyInspection] = useSaveDailyInspectionMutation();
  const slideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Reset view state when the drawer opens for a new date
  useEffect(() => {
    if (isOpen) {
      titleTimersRef.current.forEach(clearTimeout);
      titleTimersRef.current = [];
      if (slideTimerRef.current) clearTimeout(slideTimerRef.current);
      setSlideOffset(0);
      setActiveEditView(null);
      setDrawerTitle(selectedDate.format("dddd, MMMM D, YYYY"));
      setTitlePhase("idle");
      // If initial view is specified, navigate to it after a brief delay
      if (initialView) {
        const timer = setTimeout(() => {
          if (initialView === CDrawerView.ATTENDANCE_EDIT) {
            goToAttendanceEdit();
          } else if (initialView === CDrawerView.DAILY_INSPECTION_EDIT) {
            goToDailyInspectionEdit();
          }
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, selectedDate.valueOf(), initialView]);

  // Sync isClosed when the inspection record is newly created or the date changes
  useEffect(() => {
    setIsClosed(dailyInspection?.isClosed ?? false);
  }, [selectedDate.valueOf(), dailyInspection?.id]);

  const animateTitle = (newTitle: string) => {
    titleTimersRef.current.forEach(clearTimeout);
    titleTimersRef.current = [];
    setTitlePhase("out");
    titleTimersRef.current.push(
      setTimeout(() => {
        setDrawerTitle(newTitle);
        setTitlePhase("in");
        titleTimersRef.current.push(setTimeout(() => setTitlePhase("idle"), 200));
      }, 150),
    );
  };

  const goToDayDetail = () => {
    animateTitle(selectedDate.format("dddd, MMMM D, YYYY"));
    setSlideOffset(0);
    slideTimerRef.current = setTimeout(() => setActiveEditView(null), 300);
  };

  const goToAttendanceEdit = () => {
    if (slideTimerRef.current) clearTimeout(slideTimerRef.current);
    animateTitle("Edit Attendance");
    setActiveEditView(CDrawerView.ATTENDANCE_EDIT);
    requestAnimationFrame(() => requestAnimationFrame(() => setSlideOffset(-100)));
  };

  const goToDailyInspectionEdit = () => {
    if (slideTimerRef.current) clearTimeout(slideTimerRef.current);
    animateTitle("Daily Inspection");
    setActiveEditView(CDrawerView.DAILY_INSPECTION_EDIT);
    requestAnimationFrame(() => requestAnimationFrame(() => setSlideOffset(-100)));
  };

  const handleIsClosedChange = async (newValue: boolean) => {
    setIsClosed(newValue);
    try {
      const dateStr = selectedDate.format("YYYY-MM-DDTHH:mm:ss");
      const record: DailyInspection = {
        ...(dailyInspection ?? {
          id: 0,
          deleted: false,
          inspectorName: "Debbie Burke",
          inspectionCentralDateTime: dateStr,
          inspectionCompletedCentralDateTime: dateStr,
          createdCentralDateTime: "2000-01-01", // api sets this on save
          modifiedCentralDateTime: "2000-01-01", // api sets this on save
        }),
        inspectionCentralDateTime: dateStr,
        isClosed: newValue,
      };
      await saveDailyInspection(record).unwrap();
    } catch {
      // revert optimistic toggle on failure
      setIsClosed(!newValue);
    }
  };

  const handleClose = () => {
    if (slideTimerRef.current) clearTimeout(slideTimerRef.current);
    onClose();
  };

  const handleBackdropClick = () => {
    // Only close on backdrop click if not in a subform
    if (activeEditView === null) {
      handleClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleBackdropClick}
      sx={{ zIndex: 1550 }}
      slotProps={{
        paper: {
          sx: {
            width: isSmallScreen ? "100%" : "400px",
            zIndex: 1550,
          },
        },
        backdrop: {
          sx: { zIndex: 1510 },
        },
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.5 }}>
        <IconButton
          onClick={goToDayDetail}
          size="small"
          sx={{
            mr: 1,
            opacity: activeEditView !== null ? 1 : 0,
            pointerEvents: activeEditView !== null ? "auto" : "none",
            transition: "opacity 200ms",
          }}
        >
          <Box sx={{ fontSize: "1.25rem" }}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </Box>
        </IconButton>
        <Typography
          variant="h6"
          sx={{
            flexGrow: 1,
            animation: titlePhase === "out" ? `${blurOut} 150ms ease forwards` : titlePhase === "in" ? `${blurIn} 200ms ease forwards` : "none",
          }}
        >
          {drawerTitle}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            width: 44,
            height: 44,
            opacity: activeEditView === null ? 1 : 0,
            pointerEvents: activeEditView === null ? "auto" : "none",
            transition: "opacity 200ms",
          }}
        >
          <Box sx={{ fontSize: "1.25rem" }}>
            <FontAwesomeIcon icon={faXmark} />
          </Box>
        </IconButton>
      </Stack>
      <Divider />

      {/* Animated content area: two absolute-positioned slots that slide horizontally */}
      <Box sx={{ position: "relative", overflow: "hidden", flex: 1 }}>
        {/* Day Detail slot — translateX(slideOffset%) */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            transform: `translateX(${slideOffset}%)`,
            transition: "transform 300ms ease-in-out",
            overflowY: "auto",
            p: 2,
          }}
        >
          <Stack gap={2} sx={{ minHeight: "100%" }}>
            {/* Daycare Status section */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Daycare Status
              </Typography>
              <FormControlLabel
                control={<Switch checked={!isClosed} onChange={(e) => handleIsClosedChange(!e.target.checked)} />}
                label={isClosed ? "Closed" : "Open"}
              />
            </Box>
            <Divider />

            {/* Attendance section */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Attendance
                </Typography>
                <Button variant="outlined" size="small" onClick={goToAttendanceEdit}>
                  Edit
                </Button>
              </Stack>
              <AttendanceSummary attendanceLogEntries={attendanceLogEntries} children={children} />
            </Box>
            <Divider />

            {/* Daily Inspection section */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Daily Inspection
                </Typography>
                <Button variant="outlined" size="small" onClick={goToDailyInspectionEdit}>
                  Edit
                </Button>
              </Stack>
              <DailyInspectionSummary dailyInspection={dailyInspection} selectedDate={selectedDate} />
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 8 }} />

            <Button variant="outlined" onClick={handleClose} startIcon={<FontAwesomeIcon icon={faXmark} />} sx={{ height: 50, width: "100%" }}>
              Close
            </Button>
          </Stack>
        </Box>

        {/* Edit slot — translateX(slideOffset + 100%) */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            transform: `translateX(${slideOffset + 100}%)`,
            transition: "transform 300ms ease-in-out",
            display: "flex",
            flexDirection: "column",
            p: 2,
            overflowY: "auto",
          }}
        >
          {activeEditView === CDrawerView.ATTENDANCE_EDIT && <EditAttendanceForm date={selectedDate} onBack={goToDayDetail} />}
          {activeEditView === CDrawerView.DAILY_INSPECTION_EDIT && (
            <DailyInspectionEditView dailyInspection={dailyInspection} selectedDate={selectedDate} isClosed={isClosed} onBack={goToDayDetail} />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default DayDetailDrawer;

// ---------------------------------------------------------------------------
// AttendanceSummary — read-only attendance rows
// ---------------------------------------------------------------------------

function AttendanceSummary(props: { attendanceLogEntries: AttendanceLogEntry[]; children: Child[] }) {
  const { attendanceLogEntries, children } = props;

  if (children.length === 0 && attendanceLogEntries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No attendance data
      </Typography>
    );
  }

  const activeChildIds = children.filter((c) => c.isActive).map((c) => c.id);
  const entryChildIds = attendanceLogEntries.map((e) => e.childId);
  const allChildIds = _.uniq([...activeChildIds, ...entryChildIds]);
  const childrenToList = _.orderBy(
    children.filter((c) => allChildIds.includes(c.id)),
    "firstName",
    "asc",
  );

  return (
    <Stack gap={0.75}>
      {childrenToList.map((child) => {
        const entry = attendanceLogEntries.find((e) => e.childId === child.id);
        return <AttendanceRow key={child.id} child={child} entry={entry} />;
      })}
    </Stack>
  );
}

function AttendanceRow(props: { child: Child; entry?: AttendanceLogEntry }) {
  const { child, entry } = props;
  const isAbsent = !entry;
  const isSignedIn = !!entry && !entry.timeOutCentralDateTime;

  let dotColor: string;
  let timeLabel: React.ReactNode;
  if (isAbsent) {
    dotColor = grey[500];
    timeLabel = (
      <Typography variant="body2" color="text.secondary">
        Absent
      </Typography>
    );
  } else if (isSignedIn) {
    dotColor = green[500];
    const timeIn = entry.timeInCentralDateTime ? dayjs(entry.timeInCentralDateTime).format("h:mm A") : "";
    timeLabel = (
      <Typography variant="body2" color="text.secondary">
        {timeIn}
      </Typography>
    );
  } else {
    dotColor = yellow[700];
    const timeIn = entry.timeInCentralDateTime ? dayjs(entry.timeInCentralDateTime).format("h:mm A") : "";
    const timeOut = entry.timeOutCentralDateTime ? dayjs(entry.timeOutCentralDateTime).format("h:mm A") : "";
    timeLabel = (
      <Typography variant="body2" color="text.secondary">
        {timeIn} – {timeOut}
      </Typography>
    );
  }

  const lastInitial = child.lastName.length > 0 ? child.lastName[0].toUpperCase() : "";

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <StatusDot color={dotColor} />
      <Typography variant="body2" sx={{ minWidth: 90 }}>
        {child.firstName} {lastInitial}.
      </Typography>
      {timeLabel}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// DailyInspectionSummary — read-only inspection details
// ---------------------------------------------------------------------------

function DailyInspectionSummary(props: { dailyInspection: DailyInspection | undefined; selectedDate: Dayjs }) {
  const { dailyInspection, selectedDate } = props;

  if (!dailyInspection) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not recorded
      </Typography>
    );
  }

  const isCompleted = !!dailyInspection.inspectionCompletedCentralDateTime && dayjs(dailyInspection.inspectionCompletedCentralDateTime).year() > 2000;

  let completedAtLabel: string;
  if (isCompleted) {
    const completedAt = dayjs(dailyInspection.inspectionCompletedCentralDateTime);
    const isSameDay = completedAt.format("YYYY-MM-DD") === selectedDate.format("YYYY-MM-DD");
    completedAtLabel = isSameDay ? completedAt.format("h:mm A") : completedAt.format("MMM D, YYYY [at] h:mm A");
  } else {
    completedAtLabel = "Not yet completed";
  }

  return (
    <Stack gap={0.5}>
      <Stack direction="row" gap={1}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
          Status
        </Typography>
        <Typography variant="body2">{isCompleted ? "Completed" : "Not completed"}</Typography>
      </Stack>
      {dailyInspection.inspectorName && (
        <Stack direction="row" gap={1}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
            Inspector
          </Typography>
          <Typography variant="body2">{dailyInspection.inspectorName}</Typography>
        </Stack>
      )}
      <Stack direction="row" gap={1}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
          Completed at
        </Typography>
        <Typography variant="body2">{completedAtLabel}</Typography>
      </Stack>
    </Stack>
  );
}
